import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { toast } from "react-toastify";

import { OptionalRender } from "../../../components/Common/OptionalRender";
import { PageTitle } from "../../../components/Common/PageTitle";
import OrganizationFeaturesContent from "../../../components/EXPERIMENTAL/OrganizationFeatures/Content";
import FeatureSelect from "../../../components/EXPERIMENTAL/OrganizationFeatures/FeatureSelect";
import { useExperimentalOrganizationFeature } from "../../../components/EXPERIMENTAL/OrganizationFeatures/hooks/useExperimentalOrganizationFeature";
import { FeatureNames } from "../../../components/EXPERIMENTAL/OrganizationFeatures/types/featureNames";
import { KeywordReviewCrossAnalysisV2Data } from "../../../components/EXPERIMENTAL/OrganizationFeatures/types/keywordReviewCrossAnalysisV2Data";
import { useMeWithOrganizationImpersonation } from "../../../hooks/organizationImpersonation";
import { useValidateOrganization } from "../../../hooks/validateUserOrganization";
import { AccountType } from "../../../models/AccountData";
import DataSvc from "../../../services/dataSvc";
import "./styles.scss";

type IOrganizationFeaturesPageProps = RouteComponentProps<any>;

const OrganizationFeaturesPage: React.FunctionComponent<
  IOrganizationFeaturesPageProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`EXPERIMENTAL_organizationFeaturesPage.${key}`);
  const matchOrganizationId = props.match.params.organizationId;

  const me = useMeWithOrganizationImpersonation();

  useValidateOrganization({
    matchOrganizationId,
    redirectUrlFormat: (orgId) =>
      `/experimental/organizationFeatures/organization/${orgId}`,
  });

  const [selectedFeature, setSelectedFeature] = useState<FeatureNames>(
    FeatureNames.KEYWORD_REVIEW_CROSS_ANALYSIS
  );

  const { data: featureData, generate } = useExperimentalOrganizationFeature(
    selectedFeature,
    me?.organization_id
  );

  const handleGenerate = async () => {
    await generate();
  };

  const queryClient = useQueryClient();
  const v2FileInputRef = useRef<HTMLInputElement>(null);

  const uploadV2 = useMutation({
    mutationFn: (data: KeywordReviewCrossAnalysisV2Data) =>
      DataSvc.EXPERIMENTAL_uploadOrganizationFeatureData({
        featureName: FeatureNames.KEYWORD_REVIEW_CROSS_ANALYSIS_V2,
        organizationId: me?.organization_id ?? "",
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "experimentalOrganizationFeature",
        FeatureNames.KEYWORD_REVIEW_CROSS_ANALYSIS_V2,
        me?.organization_id,
      ]);
    },
  });

  const handleV2FileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(
          event.target?.result as string
        ) as KeywordReviewCrossAnalysisV2Data;
        uploadV2.mutate(parsed);
      } catch {
        toast.error(t("v2.invalid_json"));
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <div className="right-content experimental-organization-features-page">
      <div className="top-title-bar">
        <PageTitle text={t("EXPERIMENTAL_organization_features")} />
      </div>
      <div className="admin-note">{t("admin_note")}</div>
      <div>
        <div className="feature-select-container">
          <FeatureSelect
            feature={selectedFeature}
            onChange={setSelectedFeature}
          />
          {selectedFeature === FeatureNames.KEYWORD_REVIEW_CROSS_ANALYSIS_V2 ? (
            <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
              <label className="btn btn-border">
                {uploadV2.isLoading ? t("v2.uploading") : t("v2.upload_json")}
                <input
                  ref={v2FileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleV2FileChange}
                  disabled={uploadV2.isLoading}
                  style={{ display: "none" }}
                />
              </label>
            </OptionalRender>
          ) : (
            <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
              <button className="btn btn-border" onClick={handleGenerate}>
                {t("generate")}
              </button>
            </OptionalRender>
          )}
        </div>
      </div>
      <OrganizationFeaturesContent
        featureName={selectedFeature}
        data={featureData?.data}
        organizationId={me?.organization_id}
      />
    </div>
  );
};

export default withRouter(OrganizationFeaturesPage);
